import React from "react";
import { inject, observer } from "mobx-react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import { observable, toJS, reaction } from "mobx";
import moment from "moment";
import { Spinner, Alert, Intent, Icon } from "@blueprintjs/core";
import Pagination from "components/Pagination";
import { thumbnailSrcImageForImage, fileNameFromUri } from "helpers/strings";
import { formatTime } from "helpers/time";
import JobEmpty from 'components/Job/JobEmpty';

const IMAGE_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
  "tiff",
  "tif",
];

function getExtension(url) {
  if (!url) return "";
  return (
    url
      .split("?")[0]
      .match(/\.([^.]+)$/)?.[1]
      ?.toLowerCase() || ""
  );
}

function isImageFile(asset) {
  return IMAGE_EXTENSIONS.includes(getExtension(asset?.url));
}

function getFileDisplayName(file, asset) {
  const a = file.attributes || {};
  const raw =
    a.filename ||
    a.file_name ||
    a.original_filename ||
    a.name ||
    fileNameFromUri(asset?.url || "");
  const trimmed = String(raw || "").trim();
  return trimmed || "Untitled file";
}

function getUploadedTimestamp(file) {
  const a = file.attributes || {};
  return a.created_at || a.uploaded_at || a.updated_at;
}

function fileTypeDescription(ext, isImage) {
  const e = (ext || "").toLowerCase();
  if (isImage) {
    const map = {
      png: "PNG image",
      jpg: "JPEG image",
      jpeg: "JPEG image",
      gif: "GIF image",
      webp: "WebP image",
      bmp: "Bitmap image",
      tiff: "TIFF image",
      tif: "TIFF image",
    };
    return map[e] || "Image";
  }
  const docMap = {
    pdf: "PDF document",
    doc: "Word document",
    docx: "Word document",
    xls: "Excel spreadsheet",
    xlsx: "Excel spreadsheet",
    csv: "CSV file",
    txt: "Text file",
    ppt: "Presentation",
    pptx: "Presentation",
  };
  return docMap[e] || (e ? `${e.toUpperCase()} file` : "File");
}

function typeTagLabel(ext, isImage) {
  if (isImage) {
    const u = (ext || "").toLowerCase();
    if (u === "jpeg") return "JPEG";
    return u ? u.toUpperCase() : "IMG";
  }
  return (ext || "FILE").toUpperCase();
}

const dayLabel = (date) => {
  const day = moment(date);
  if (day.isSame(moment(), "day")) return "Today";
  if (day.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return day.format(day.isSame(moment(), "year") ? "ddd, MMM D" : "ddd, MMM D, YYYY");
};

// Document type tile colour: PDF red, Word blue, spreadsheets green.
const docTone = (ext) => {
  const e = (ext || "").toLowerCase();
  if (e === "pdf") return "red";
  if (e === "doc" || e === "docx" || e === "rtf" || e === "odt") return "blue";
  if (e === "xls" || e === "xlsx" || e === "csv") return "green";
  if (e === "ppt" || e === "pptx") return "amber";
  return "grey";
};

@inject("viewStore", "jobStore")
@observer
class ImagesTable extends React.Component {
  @observable isLoading = true;
  @observable files = [];
  @observable lightboxSlides = [];
  @observable isOpen = false;
  @observable currentImage = 0;
  @observable isDeleteDialogVisible = false;
  @observable page = 1;
  perPage = 24; // a grid's worth of photos
  indexToDelete = null;
  meta = { total_pages: 1 };

  componentDidMount() {
    this.loadFilesFromBackend(this.page);
    const { jobStore } = this.props;
    this._uploadReaction = reaction(
      () => jobStore.imageUploadVersion,
      () => this.loadFilesFromBackend(this.page),
    );
  }

  componentDidUpdate(prevProps) {
    const previousId = prevProps.job?.attributes?.id;
    const nextId = this.props.job?.attributes?.id;
    if (nextId && nextId !== previousId) {
      this.page = 1;
      this.loadFilesFromBackend(1);
    }
  }

  componentWillUnmount() {
    this._uploadReaction && this._uploadReaction();
  }

  loadFilesFromBackend(page) {
    const request = (this._filesRequest || 0) + 1;
    this._filesRequest = request;
    const { jobStore } = this.props;
    this.isLoading = true;
    jobStore
      .loadImages({ page, per_page: this.perPage })
      .then((response) => {
        if (request !== this._filesRequest) return;
        this.files = response.data.data;
        this.buildLightboxSlides();
        this.meta = response.data.meta || { total_pages: 1 };
        this.isLoading = false;
      })
      .catch(() => {
        if (request !== this._filesRequest) return;
        this.isLoading = false;
      });
  }

  buildLightboxSlides() {
    this.lightboxSlides = this.files
      .filter((f) => isImageFile(f.attributes.asset))
      .map((f) => ({
        src: f.attributes.asset.url,
        thumbnail: f.attributes.asset.thumb?.url || f.attributes.asset.url,
      }));
  }

  getLightboxIndex(fileIndex) {
    let count = 0;
    for (let i = 0; i < fileIndex; i++) {
      if (isImageFile(this.files[i]?.attributes.asset)) count++;
    }
    return count;
  }

  handlePageChange = (newPage) => {
    if (
      newPage !== this.page &&
      newPage >= 1 &&
      newPage <= this.meta.total_pages
    ) {
      this.page = newPage;
      this.loadFilesFromBackend(newPage);
    }
  };

  openLightbox(fileIndex) {
    this.currentImage = this.getLightboxIndex(fileIndex);
    this.isOpen = true;
  }

  closeLightbox() {
    this.isOpen = false;
  }

  openFileAtIndex(index) {
    const asset = this.files[index]?.attributes?.asset;
    if (!asset?.url) return;
    if (isImageFile(asset)) {
      this.openLightbox(index);
    } else {
      window.open(asset.url, "_blank", "noopener,noreferrer");
    }
  }

  startDelete(index) {
    this.indexToDelete = index;
    this.isDeleteDialogVisible = true;
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleDeleteConfirm() {
    let {
      jobStore,
      jobStore: {
        job: { object },
      },
    } = this.props;
    this.isDeleteDialogVisible = false;
    this.isLoading = true;
    const file = this.files[this.indexToDelete];
    const deletePromise = file.attributes.scheduled_event_id
      ? jobStore.deleteJobImage(file.id)
      : jobStore.deleteImage(file.attributes.work_report_id, file.id);
    deletePromise
      .then(() => {
        jobStore.refreshJob({ uuid: object.attributes.id }).then((result) => {
          if (!result.applied) {
            this.isLoading = false;
            return;
          }
          this.files.splice(this.indexToDelete, 1);
          if (this.files.length < 1 && this.page > 1) {
            this.handlePageChange(this.page - 1);
          } else {
            this.buildLightboxSlides();
            this.isLoading = false;
          }
        });
      })
      .catch(() => {
        this.isLoading = false;
      });
  }

  render() {
    if (this.isLoading) {
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 120, padding: 24 }}
        >
          <Spinner size={25} />
        </div>
      );
    }

    if (this.files.length === 0) {
      return (
        <JobEmpty kind="files" />
      );
    }

    // By the day they were added, newest first; photos as a grid, other files
    // as rows under them.
    const days = [];
    this.files.forEach((file, index) => {
      const uploadedAt = getUploadedTimestamp(file);
      const key = uploadedAt ? moment(uploadedAt).format('YYYY-MM-DD') : 'unknown';
      let day = days.find((d) => d.key === key);
      if (!day) {
        day = { key, at: uploadedAt, photos: [], documents: [] };
        days.push(day);
      }
      (isImageFile(file.attributes.asset) ? day.photos : day.documents).push({ file, index });
    });
    days.sort((x, y) => (y.at ? new Date(y.at) : 0) - (x.at ? new Date(x.at) : 0));

    return (
      <React.Fragment>
        <div className="jpf-files">
          {days.map((day) => (
            <section key={day.key} className="jpf-day" aria-label={day.at ? moment(day.at).format('dddd, MMMM D') : 'Files'}>
              <h3 className="tl-day__head">
                <span>{day.at ? dayLabel(day.at) : 'Undated'}</span>
                <span className="tl-day__total">
                  {[
                    day.photos.length && `${day.photos.length} ${day.photos.length === 1 ? 'photo' : 'photos'}`,
                    day.documents.length && `${day.documents.length} ${day.documents.length === 1 ? 'document' : 'documents'}`,
                  ].filter(Boolean).join(', ')}
                </span>
              </h3>

              {day.photos.length > 0 && (
                <ul className="jpf-grid">
                  {day.photos.map(({ file, index }) => {
                    const asset = file.attributes.asset;
                    const name = getFileDisplayName(file, asset);
                    const uploadedAt = getUploadedTimestamp(file);
                    return (
                      <li key={file.id || index} className="jpf-photo">
                        <button
                          type="button"
                          className="jpf-photo__open"
                          onClick={() => this.openFileAtIndex(index)}
                          title={`${name}${uploadedAt ? `, added ${formatTime(uploadedAt)}` : ''}`}
                          aria-label={`Open photo ${name}`}
                        >
                          <img src={thumbnailSrcImageForImage(asset)} alt="" loading="lazy" />
                          <span className="jpf-photo__meta">
                            {uploadedAt && <span>{moment(uploadedAt).format('h:mm A')}</span>}
                            {file.attributes.work_report_id && <span className="jpf-from">Work order</span>}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="jpf-delete"
                          onClick={() => this.startDelete(index)}
                          aria-label={`Delete photo ${name}`}
                          title="Delete"
                        >
                          <Icon icon="trash" iconSize={12} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {day.documents.length > 0 && (
                <ul className="jpf-docs">
                  {day.documents.map(({ file, index }) => {
                    const asset = file.attributes.asset;
                    const ext = getExtension(asset?.url);
                    const name = getFileDisplayName(file, asset);
                    const uploadedAt = getUploadedTimestamp(file);
                    return (
                      <li key={file.id || index} className="jpf-doc">
                        <span className={`jpf-doc__type jpf-doc__type--${docTone(ext)}`} aria-hidden="true">{typeTagLabel(ext, false)}</span>
                        <span className="jpf-doc__text">
                          <button type="button" className="jpf-doc__name" onClick={() => this.openFileAtIndex(index)} title={name}>
                            {name}
                          </button>
                          <span className="jpf-doc__meta">
                            {fileTypeDescription(ext, false)}
                            {uploadedAt && <span title={formatTime(uploadedAt)}>{moment(uploadedAt).format('h:mm A')}</span>}
                            {file.attributes.work_report_id && <span className="jpf-from">Work order</span>}
                          </span>
                        </span>
                        <button type="button" className="fb-inline-action jpf-doc__open" onClick={() => this.openFileAtIndex(index)}>
                          <Icon icon="share" iconSize={12} /> Open
                        </button>
                        <button
                          type="button"
                          className="jpf-delete jpf-delete--row"
                          onClick={() => this.startDelete(index)}
                          aria-label={`Delete ${name}`}
                          title="Delete"
                        >
                          <Icon icon="trash" iconSize={12} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          ))}
          {this.meta.total_pages > 1 && (
            <div className="jpf-pages">
              <Pagination value={this.page} total={this.meta.total_pages} onChange={this.handlePageChange} />
            </div>
          )}
        </div>

        <Lightbox
          open={this.isOpen}
          close={this.closeLightbox.bind(this)}
          slides={toJS(this.lightboxSlides)}
          plugins={[Zoom, Thumbnails]}
          index={this.currentImage}
          zoom={{ maxZoomPixelRatio: 10, zoomInMultiplier: 1.5 }}
        />

        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete file"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <strong>Delete this file?</strong>
          <p>It’s removed from the job and can’t be brought back.</p>
        </Alert>
      </React.Fragment>
    );
  }
}

export default ImagesTable;
